import { SaveOutlined as SaveOutlinedIcon } from '@mui/icons-material';
import { AlertColor, Box, Button } from '@mui/material';
import React, { FC, useState } from 'react';
import DrawerMenu from 'components/DrawerMenu';
import InputField from 'components/InputField';
import Notification from 'components/Notification';
import axiosInstance from 'utils/axios';
import { URLS } from 'utils/constants/urls';
import strings from 'common/Translation/Translate';

interface CommentDrawerProps {
  open: boolean;
  onClose: () => void;
  salesCallId: number | null;
  onSuccess?: () => void | Promise<void>;
  title?: string;
  width?: string;
}

const CommentDrawer: FC<CommentDrawerProps> = ({
  open,
  onClose,
  salesCallId,
  onSuccess,
  title = 'Comments',
  width = '60vw',
}) => {
  const [commentInput, setCommentInput] = useState<string>('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };

  const handleAddComment = async () => {
    if (!commentInput.trim() || !salesCallId) return;
    try {
      setCommentLoading(true);
      const { status } = await axiosInstance.post(URLS.salesCallComments(salesCallId), {
        comment: commentInput,
      });
      if (status === 200) {
        setMessage({ display: true, severity: 'success', message: 'Comment added successfully' });
        setCommentInput('');
        if (onSuccess) {
          await onSuccess();
        }
        onClose();
      }
    } catch (error: any) {
      setMessage({
        display: true,
        severity: 'error',
        message: error.response?.data?.message || 'Failed to add comment',
      });
    } finally {
      setCommentLoading(false);
    }
  };

  const handleClose = () => {
    setCommentInput('');
    setMessage({ display: false, severity: null, message: '' });
    onClose();
  };

  return (
    <DrawerMenu
      width={width}
      open={open && !!salesCallId}
      handleClose={handleClose}
      closeButton={true}
      title={title}
      dropBack={false}>
      <Box sx={{ p: 2, width: '100%' }}>
        {message.display && (
          <Notification
            isOpen={message.display}
            message={message.message}
            severity={message.severity as AlertColor}
            closeNotification={closeNotification}
            autoHideDuration={2000}
          />
        )}
        <InputField
          value={commentInput}
          onChange={(e: any) => setCommentInput(e.target.value)}
          label="Add comments"
          rows={2}
          multiline
          sx={{ width: '100%', maxWidth: '100%' }}
        />
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
          <Button
            variant="contained"
            size="medium"
            onClick={handleAddComment}
            disabled={!commentInput.trim() || commentLoading}
            sx={{ textTransform: 'capitalize' }}
            endIcon={<SaveOutlinedIcon />}>
            {strings.sales_call_add_comments || 'Add Comment'}
          </Button>
        </Box>
      </Box>
    </DrawerMenu>
  );
};

export default CommentDrawer;
